/**
 * PerspectiveInfoDialog — small (?) icon + explainer dialog for the
 * Relationship Perspective sheet. Sits to the LEFT of the SheetContent
 * close (X) button.
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  HelpCircle,
  Lock,
  Gauge,
  Hourglass,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

export default function PerspectiveInfoDialog() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="What is this panel?"
        aria-label="About the Relationship Perspective panel"
        data-testid="perspective-info-btn"
        className="absolute right-0 top-0 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full text-white/55 hover:text-divine-gold hover:bg-divine-gold/10 transition-colors focus:outline-none focus:ring-2 focus:ring-divine-gold/40"
      >
        <HelpCircle className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-divine-charcoal border-divine-gold/25 text-white/85 max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="perspective-info-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              About the Relationship Perspective
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-relaxed">
            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-1.5">
                What you're looking at
              </h4>
              <p>
                A live, structured snapshot of what{" "}
                <span className="text-divine-gold">Agent Eros</span> currently
                believes about this relationship — built up over time from
                your coaching sessions and your own notes. Each row is a{" "}
                <em>claim</em>: one observation in one domain (communication,
                trust, intimacy, growth edges, etc.), with the evidence that
                supports it and a confidence rating.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-1.5">
                How it works behind the scenes
              </h4>
              <p>
                After every coaching session, Eros analyses what was said and
                updates this panel — creating new claims, corroborating old
                ones, lowering confidence when something contradicts, or
                retiring claims that no longer hold. The panel is{" "}
                <span className="text-divine-gold/80">
                  also the working memory the next session reads from
                </span>{" "}
                — so the more accurate it is, the more grounded your coaching
                will feel.
              </p>
              <p className="mt-2 text-white/55 text-xs">
                You can also bootstrap it directly via{" "}
                <em>Seed claims from your notes</em> at the bottom of the
                panel — paste anything you already know about this person and
                Eros will structure it for you to review.
              </p>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-2">
                Your controls (per claim)
              </h4>
              <ul className="space-y-3">
                <li className="flex gap-2.5">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0 text-divine-gold/80" />
                  <div>
                    <span className="text-divine-gold font-medium">Lock</span>
                    <p className="text-white/70 text-xs mt-0.5">
                      Pin a claim as immutable. Eros will never overwrite,
                      decay, or contradict it from automatic processing. Use
                      this when you're certain — locked claims are the
                      foundation Eros builds everything else around.
                    </p>
                  </div>
                </li>

                <li className="flex gap-2.5">
                  <Gauge className="h-4 w-4 mt-0.5 shrink-0 text-divine-gold/80" />
                  <div>
                    <span className="text-divine-gold font-medium">
                      Override confidence
                    </span>
                    <p className="text-white/70 text-xs mt-0.5">
                      Manually set how strongly Eros should weigh this claim
                      (low / medium / high). Eros tends to be cautious;
                      override when you know more than the conversation has
                      surfaced. High confidence = treated as near-fact in
                      coaching; low = a working hypothesis.
                    </p>
                  </div>
                </li>

                <li className="flex gap-2.5">
                  <Hourglass className="h-4 w-4 mt-0.5 shrink-0 text-divine-gold/80" />
                  <div>
                    <span className="text-divine-gold font-medium">
                      Override decay
                    </span>
                    <p className="text-white/70 text-xs mt-0.5">
                      How long (in days) before Eros automatically flags this
                      claim as stale and re-asks. Default depends on the
                      domain — for fast-moving things (mood, conflict) it's
                      short; for durable things (values, life-goals) it's
                      long. Increase the decay window for claims you don't
                      want re-probed often.
                    </p>
                  </div>
                </li>

                <li className="flex gap-2.5 border border-emerald-500/30 bg-emerald-500/5 rounded-md p-3 -mx-1">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" />
                  <div>
                    <span className="text-emerald-300 font-medium">
                      Resolve{" "}
                      <span className="text-emerald-200/60 text-[10px] uppercase tracking-wide ml-1">
                        the most important one
                      </span>
                    </span>
                    <p className="text-white/80 text-xs mt-1">
                      Mark a thread as <em>worked through</em> — not wrong,
                      not forgotten, but{" "}
                      <span className="text-emerald-300">
                        actively closed
                      </span>
                      . Resolving is how you tell Eros "we've dealt with
                      this; stop steering coaching around it."
                    </p>
                    <p className="text-white/65 text-xs mt-2">
                      <span className="text-emerald-300/90 font-medium">
                        What changes when you resolve:
                      </span>{" "}
                      the claim disappears from Eros's working model (no
                      longer in the live coaching prompt, no longer in the
                      next session's input). But the resolution itself is{" "}
                      <span className="text-emerald-300/90">archived</span> —
                      Eros knows you closed it on purpose, with your
                      reasoning recorded as evidence of progress.
                    </p>
                    <p className="text-white/55 text-xs mt-2 italic">
                      Think of it as graduating a topic, not deleting it. If
                      the same issue re-emerges later, Eros will recognise
                      that and treat it as a new chapter rather than
                      resurrecting the old one.
                    </p>
                  </div>
                </li>
              </ul>
            </section>

            <section>
              <h4 className="text-divine-gold/90 text-xs uppercase tracking-wide mb-1.5">
                Two more actions
              </h4>
              <p className="text-white/70 text-xs">
                <span className="text-white/85">Edit</span> rewrites the
                claim text in your own words — useful when Eros is roughly
                right but not phrased the way you'd put it.{" "}
                <span className="text-white/85">Retract</span> is the
                opposite of Resolve: this claim was{" "}
                <em>wrong</em>, not closed. Retracting tells Eros to learn
                from the mistake.
              </p>
            </section>

            <p className="text-white/40 text-[11px] pt-2 border-t border-divine-gold/10">
              Confidence colours: emerald = high (≥ 75%), amber = medium
              (≥ 50%), grey = low. The 🔒 / ✏ icons next to a claim mean
              "locked by you" / "edited by you" — Eros leaves those alone.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
